import styles from './Dashboard.module.css';
import toneLogo from '../../assests/images/tone-logo.png';
import image6 from '../../assests/images/Image 6.png';
import image7 from '../../assests/images/Image 7.png';
import { useNavigate, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllModuleTrigger } from '../../slices/moduleSlice';
import { AppDispatch, RootState } from '../../store/index';
import { useEffect, useCallback, useState, useRef } from 'react';
import { Menu, Module } from '../../dtos/module';
import { CONSTANTS } from '../../utils/constants';
import { getAuthDetails } from '../../utils/helper';
import toneicon from '../../assests/images/tone-icon.png';
import InlineSpinner from '../Layout/InlineSpinner';
import UserActivity from '../userActivity/UserActivity';
import { useTheme } from '../../contexts/ThemeContext';
import { useAlert, createAlertHelpers } from '../Layout/useAlert';

const DashBoardPage = () => {
  const { colors, updateColors, isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const isAuthenticated = getAuthDetails();
  const dispatch = useDispatch<AppDispatch>();
  const { showAlert, AlertComponent } = useAlert();
  const alertHelpers = createAlertHelpers(showAlert);
  const modules = useSelector((state: RootState) => state.module.moduleList);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const { addUserActivity } = UserActivity();

  const handleNavigate = useCallback(
    (path: string, menuDetails: Menu[] | undefined, modules: Module) => {
      const isSuperAdmin = isAuthenticated?.user?.roleId === 1;

      console.log('handleNavigate called:', {
        path,
        moduleName: modules.name,
        moduleSlug: modules.slug,
        menuCount: menuDetails?.length || 0,
        isSuperAdmin,
      });

      if (!menuDetails || menuDetails.length === 0) {
        if (isSuperAdmin) {
          // Super admin can access modules even without menus
          localStorage.setItem('menuName', modules.name || 'Unknown');
          addUserActivity(
            { module: modules },
            `select ${modules.name} Module`,
            'Home page click event',
            'Home',
          );
          console.log('Super admin navigating to:', path);
          navigate(path);
        } else {
          alertHelpers.error(
            'Access Denied',
            `You don't have permission to access any menus under the ${modules.name} module. Please contact Admin.`,
          );
        }
        return;
      } else if (menuDetails && menuDetails.length > 0) {
        localStorage.setItem('menuName', menuDetails[0]?.name || 'Unknown');
        addUserActivity(
          { menu: menuDetails[0] || {} },
          `select ${modules.name} Module`,
          'Home page click event',
          'Home',
        );
      }
      console.log('Regular navigation to:', path);
      navigate(path);
    },
    [navigate, isAuthenticated],
  );

  useEffect(() => {
    dispatch(getAllModuleTrigger()).finally(() => setLoading(false));
  }, [dispatch]);

  // Debug: Log modules data (only once)
  useEffect(() => {
    if (modules.data && modules.data.length > 0) {
      console.log(
        'Available modules:',
        modules.data.map((m) => ({ name: m.name, slug: m.slug, menus: m.menus?.length || 0 })),
      );
    }
  }, [modules.data]);

  useEffect(() => {
    updateColors({
      ...colors,
      primary: getAuthDetails().user.primaryColor
        ? getAuthDetails().user.primaryColor
        : CONSTANTS.DEFAULT_PRIMARY_COLOR,
      secondary: getAuthDetails().user.secondaryColor
        ? getAuthDetails().user.secondaryColor
        : CONSTANTS.DEFAULT_SECONDARY_COLOR,
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleEmitterSoftwareClick = () => {
    localStorage.setItem('menuName', 'Emitter software');
    addUserActivity(
      {},
      'Open Emitter software',
      'User opened the protected emitter software download walkthrough.',
      'Home',
    );
    navigate('/emitter-software');
  };

  function signOut() {
    alertHelpers.logoutConfirm().then((result) => {
      if (result.isConfirmed) {
        document.documentElement.style.setProperty(
          '--primary-color',
          CONSTANTS.DEFAULT_PRIMARY_COLOR,
        );
        document.documentElement.style.setProperty(
          '--secondary-color',
          CONSTANTS.DEFAULT_SECONDARY_COLOR,
        );
        localStorage.removeItem('otpVerified');
        navigate('/logout');
        alertHelpers.success('Logged Out', 'You have been successfully logged out.');
        addUserActivity(
          {},
          'User Logged Out',
          'User has successfully logged out from the application.',
          'Profile',
        );
      }
    });
  }

  const profilePath = isAuthenticated?.user?.profile;
  const cfBaseUrl = process.env.REACT_APP_CF_STORAGE_BASE_URL;
  const profileImageUrl = profilePath
    ? `${cfBaseUrl}/${process.env.REACT_APP_PROFILE_FOLDER}/${profilePath.replace(/\\/g, '/')}`
    : '';

  return (
    <div className={styles.mainDashboard}>
      <AlertComponent />
      <div className={styles.leftBackground}>
        <img src={image6} alt="image54" />
      </div>
      <div className={styles.rightBackground}>
        <img src={image7} alt="image5" />
      </div>
      <div className={styles.sideNav} id="navbarNav">
        <ul className={styles.navList}>
          <li className={styles.navItem}>
            <ul className={styles.navListUnstyled}>
              <li className={styles.dropdown} ref={dropdownRef}>
                <button
                  className={styles.dropdownToggle}
                  onClick={toggleDropdown}
                  role="button"
                  id="dropdownUser"
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                >
                  <div className={`${styles.avatar} ${styles.avatarOnline}`}>
                    <img
                      alt="avatar"
                      src={profileImageUrl || toneicon}
                      className={styles.profileImg}
                    />
                  </div>
                </button>

                <div
                  className={`${styles.dropdownMenu} ${isDropdownOpen ? styles.dropdownMenuOpen : ''}`}
                  aria-labelledby="dropdownUser"
                >
                  <div className={styles.profileDrop}>
                    <div className={styles.profileInfo}>
                      <div className={`${styles.avatar} ${styles.avatarOnline}`}>
                        <img
                          alt="avatar"
                          src={profileImageUrl || toneicon}
                          className={styles.profileImg}
                        />
                      </div>
                      <div>
                        <h6 className={styles.profileName}>
                          {isAuthenticated.user.firstname} {isAuthenticated.user.lastname}
                        </h6>
                        <p className={styles.profileEmail}>{isAuthenticated.user.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className={styles.dropdownDivider}></div>
                  <div>
                    <ul className={`${styles.navListUnstyled} ${styles.mbSpacing}`}>
                      <li>
                        <NavLink
                          className={styles.dropdownItem}
                          to="/profile/about"
                          style={{
                            textDecoration: 'none',
                            backgroundColor: 'transparent',
                            color: 'black',
                          }}
                          onClick={() => {
                            localStorage.setItem('menuName', 'About');
                            setIsDropdownOpen(false);
                          }}
                        >
                          <span className={styles.mrSpacing}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 32 32"
                              id="profile"
                            >
                              <path d="M29.76,13A14.08,14.08,0,0,0,1.93,16a13.92,13.92,0,0,0,1.9,7c.09.16.19.31.29.47l0,0c.08.14.18.28.28.42l.09.13a14,14,0,0,0,23,0l.09-.13c.1-.14.2-.28.28-.42l0,0,.3-.47A14.18,14.18,0,0,0,29.76,13ZM16,28a12,12,0,0,1-9.8-5,12.08,12.08,0,0,1,19.6,0A12,12,0,0,1,16,28Zm10.9-6.87a14,14,0,0,0-21.79,0A12,12,0,0,1,8.24,6.76a12.17,12.17,0,0,1,9.94-2.64,12.05,12.05,0,0,1,8.72,17Z"></path>
                              <path d="M16,5a5,5,0,1,0,5,5A5,5,0,0,0,16,5Zm0,8a3,3,0,1,1,3-3A3,3,0,0,1,16,13Z"></path>
                            </svg>
                          </span>
                          Profile
                        </NavLink>
                      </li>
                    </ul>
                  </div>
                  <ul className={`${styles.navListUnstyled} ${styles.mbSpacing}`}>
                    <li>
                      <a
                        className={styles.dropdownItem}
                        onClick={() => {
                          setIsDropdownOpen(false);
                          signOut();
                        }}
                      >
                        <span className={styles.mrSpacing}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 256 256"
                            id="sign-out"
                          >
                            <rect width="256" height="256" fill="none"></rect>
                            <polyline
                              fill="none"
                              stroke="#000"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="16"
                              points="174.011 86 216 128 174.011 170"
                            ></polyline>
                            <line
                              x1="104"
                              x2="215.971"
                              y1="128"
                              y2="128"
                              fill="none"
                              stroke="#000"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="16"
                            ></line>
                            <path
                              fill="none"
                              stroke="#000"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="16"
                              d="M104,216H48a8,8,0,0,1-8-8V48a8,8,0,0,1,8-8h56"
                            ></path>
                          </svg>
                        </span>
                        Sign Out
                      </a>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </li>
          <li className={`${styles.navItem} ${styles.mbSpacing}`}>
            <div className={styles.themeToggle} onClick={toggleDarkMode}>
              <div className={styles.themeToggleContainer}>
                <div className={isDarkMode ? styles.themeToggleCheck : styles.themeToggleUncheck}>
                  <span>{isDarkMode ? '☀️' : '🌙'}</span>
                </div>
              </div>
              <div
                className={styles.themeToggleCircle}
                style={{
                  left: isDarkMode ? '27px' : '1px',
                  backgroundColor: isDarkMode ? '#4d4d4d' : '#fafafa',
                  borderColor: isDarkMode ? '#fafafa' : '#4d4d4d',
                }}
              />
              <input
                className={styles.themeToggleInput}
                type="checkbox"
                aria-label="Toggle Button"
                checked={isDarkMode}
                readOnly
              />
            </div>
          </li>
        </ul>
      </div>

      <div className={styles.dashboardContainer}>
        <div className={styles.logoSection}>
          <img src={toneLogo} alt="Logo" className={styles.logoImage} />
        </div>
        <div className={styles.dashboardGrid}>
          {loading ? (
            <div className={styles.loadingSection}>
              <InlineSpinner size="medium" />
            </div>
          ) : (
            <>
              {modules.data.length > 0 ? (
                modules.data.map((module) => (
                  <div
                    key={module.id}
                    onClick={() => {
                      const isSuperAdmin = isAuthenticated?.user?.roleId === 1;
                      let path = '/home';

                      console.log('Module click:', {
                        moduleName: module.name,
                        moduleSlug: module.slug,
                        menuCount: module.menus?.length || 0,
                        isSuperAdmin,
                      });

                      if (module.slug && module.menus && module.menus.length > 0) {
                        // If module has menus, navigate to first menu
                        path = `/${module.slug}/${module.menus[0]?.slug || ''}`;
                      } else if (isSuperAdmin && module.slug) {
                        // For super admin, navigate to specific known paths
                        if (module.slug === 'tone-dashboard') {
                          path = '/tone-dashboard/home';
                        } else if (module.slug === 'tone-portal') {
                          // TONE Portal should navigate to TONE Dashboard
                          path = '/tone-dashboard/home';
                        } else if (module.slug === 'aes') {
                          path = '/aes/generate';
                        } else if (module.slug === 'demo-admin') {
                          path = '/demo-admin/client-dashboard';
                        } else if (module.slug === 'tone-admin') {
                          path = '/tone-admin/dashboard';
                        } else if (module.slug === 'profile') {
                          path = '/profile/about';
                        } else {
                          // Fallback to module base path
                          path = `/${module.slug}`;
                        }
                      }

                      console.log('Generated path:', path);
                      handleNavigate(path, module.menus || [], module);
                    }}
                    className={styles.moduleCard}
                  >
                    <div className={styles.imageContainer}>
                      <img
                        src={`images/${module.logo}`}
                        className={styles.moduleImage}
                        alt={`${module.name} logo`}
                      />
                    </div>
                    <div className={styles.cardBody}>
                      <h5 className={styles.cardTitle}>{module.name}</h5>
                      <p className={styles.cardDescription}>{module.description}</p>
                    </div>
                    <div className={styles.cardFooter}>
                      <button className={styles.cardButton}>Open</button>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyStateCard}>
                  <div className={styles.imageContainer}></div>
                  <div className={styles.emptyStateBody}>
                    <h5 className={styles.emptyStateTitle}>Please contact Admin</h5>
                    <p className={styles.emptyStateDescription}>
                      You don&apos;t have access to any management page on this website. Please
                      contact support email {CONSTANTS.SUPPORT_EMAIL}
                    </p>
                  </div>
                  <div className={styles.emptyStateFooter}></div>
                </div>
              )}

              <button
                type="button"
                className={`${styles.moduleCard} ${styles.emitterCard}`}
                onClick={handleEmitterSoftwareClick}
                aria-label="Open Emitter software download and installation guide"
              >
                <div className={styles.imageContainer}>
                  <div className={styles.emitterIconShell}>
                    <span className={styles.emitterPulse} aria-hidden="true" />
                    <img src={toneicon} className={styles.emitterIcon} alt="" />
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <span className={styles.emitterBadge}>macOS utility</span>
                  <h5 className={styles.cardTitle}>Emitter software</h5>
                  <p className={styles.cardDescription}>
                    Download the protected emitter programmer and follow the guided installation
                    walkthrough.
                  </p>
                </div>
                <div className={styles.cardFooter}>
                  <span className={styles.cardButton}>View download</span>
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashBoardPage;
